import useSound from "use-sound";
import { m } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import Confetti from "react-dom-confetti";
import { CollectionStars } from "../../../set/components/Stars";
import { CollectionTrophy } from "../../../set/components/Trophy";

const isApple =
    typeof window !== "undefined" &&
    window.navigator.appVersion.indexOf("iPhone") > -1;

function useInterval(callback, delay, pauseTimer) {
    const savedCallback = useRef();

    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        function tick() {
            savedCallback.current();
        }
        if (delay !== null) {
            const id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay, pauseTimer]);
}

export const CorrectQuestionsCount = ({ className, children }) => {
    return (
        <m.div
            initial={{
                scale: 0,
                opacity: 0,
            }}
            animate={{
                scale: 1,
                opacity: 1,
            }}
            transition={{
                duration: 0.2,
                stiffness: 250,
                type: "spring",
            }}
            className={className}
        >
            {children}
        </m.div>
    );
};

export const CollectionStatus = ({
    correctCount,
    totalQuestions,
    starsTimeoutStart = 1000,
    fxDuration = 200,
    totalStars,
    set,
}) => {
    const [stars, setStars] = useState(0);
    const [trophy, setTrophy] = useState(null);
    const [starsAnimation, startStarsAnimation] = useState(false);
    const [trophyAnimation, startTrophyAnimation] = useState(false);
    const [confetti, startConfetti] = useState(false);
    const [playStarsSound] = useSound(
        "/assets/sounds/Star given_futuristic-cartoon-pop-3-SBA-300463184.mp3",
        { autoplay: true },
    );
    const [playTrophySound] = useSound(
        "/assets/sounds/Got Trophy_level-complete-ring-magical-sparkle-SBA-300420665.mp3",
    );
    const [playNoTrophySound] = useSound(
        "/assets/sounds/Didn't get trophy_arcade-try-again-replay-sound-4-SBA-300462888.mp3",
    );

    const confettiConfig = {
        angle: 90,
        spread: "150",
        startVelocity: 40,
        elementCount: 100 * totalStars,
        dragFriction: "0.25",
        duration: 3000,
        stagger: "3",
        width: "10px",
        height: "10px",
        perspective: "1000px",
        colors: ["#a864fd", "#29cdff", "#78ff44", "#ff718d", "#fdff6a"],
    };

    useInterval(
        () => {
            if (stars + 1 <= totalStars) {
                setStars(stars + 1);
            } else setStars(totalStars);
        },
        !starsAnimation || stars === totalStars ? null : starsTimeoutStart,
        null,
    );

    useEffect(() => {
        if (stars) playStarsSound();
        if (stars == totalStars) {
            const timeoutId = setTimeout(() => {
                setTrophy(stars);
                startConfetti(true);
            }, 1000);
            return () => {
                clearTimeout(timeoutId);
            };
        }
    }, [stars]);

    useEffect(() => {
        if (trophy !== null) {
            if (trophy == 5) playTrophySound();
            else playNoTrophySound();
        }
    }, [trophy]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            startStarsAnimation(true);
            setTimeout(() => {
                startTrophyAnimation(true);
            }, 500);
        }, 1000);
        return () => {
            clearTimeout(timeoutId);
        };
    }, []);
    return (
        <div className="flex flex-col items-center justify-center pt-16">
            <CorrectQuestionsCount className="flex flex-col items-center text-4xl font-extrabold text-center text-dark-purple mt-8 z-10">
                <span>
                    {correctCount || 0} / {totalQuestions}
                </span>
                <span>
                    {set.type == "practise_learn_practise"
                        ? "⭐️ collected"
                        : "correct"}
                </span>
            </CorrectQuestionsCount>
            {set.type == "practise_learn_practise" ? null : (
                <div className="flex flex-row items-center justify-center mt-6">
                    <CollectionStars
                        fx={starsAnimation}
                        stars={starsAnimation ? totalStars : 0}
                        className="relative w-10 h-11 z-10"
                        fxDuration={fxDuration}
                        timeoutStart={starsTimeoutStart}
                    />
                </div>
            )}
            <div className="flex flex-row items-center justify-center mt-6">
                <div className="flex flex-col relative w-32 h-36 z-10">
                    <CollectionTrophy
                        stars={isApple ? trophy : totalStars}
                        bottom="round"
                        fx={true}
                        video={isApple ? false : trophyAnimation}
                        endscreen={true}
                    />
                    <Confetti
                        className="confetti"
                        active={confetti}
                        config={confettiConfig}
                    />
                </div>
            </div>
        </div>
    );
};
